const ObjectId = require('bson-objectid');
const { Type } = require('@sinclair/typebox');
//using a class here as it allows for clean reference to this.callbacks from the methods

/**
 * Backbone metadata has many places where a foreign key is referenced. Most commonly a dropdown.
 * On those dropdowns (and other similar components),
 * very little information is available about the actual specs on the properties referenced.
 * I.e., we know there is an "_id", and we know there is one or more additional properties,
 * that on dropdowns, we assume can be at least cast to string. But that is it.
 *
 * ForeignKeyRegistry provides a mechanism to sort of lazily include those additional properties
 * when parsing the dropdown and similar components, and actually expand the information when available.
 */
module.exports = class ForeignKeyRegistry {
    callbacks = [];

    /**
     * call all callbacks for expanding lazily included properties to their full specs
     * as defined by other components  (e.g. max length, the actual data type, etc.)
     * @param {*} dataDictionary Completed data dictionary (apart from lazily included properties)
     */
    updateWithFinalDataDictionary = dataDictionary => {
        this.callbacks.forEach(cb => cb(dataDictionary));
    };

    /**
     * Builds the schema fragment used when a field points at another relation.
     *
     * Dropdown-style blocks often know only the referenced namespace/relation and the fields
     * they display, while the referenced relation's full schema may be discovered later in
     * the same metadata tree. This returns the minimal foreign-key shape immediately and
     * records a callback that enriches that shape after extraction has seen every block.
     *
     * The callback mutates the completed data dictionary in place. Keeping that side effect
     * here avoids making every form/dropdown block understand deferred schema expansion.
     * Display properties may include dotted relation paths like `metaui:useCase.title`;
     * those must become nested JSON object schemas because API payloads store
     * `{ "metaui:useCase": { "title": ... } }`, not flat dotted keys.
     *
     * @param {string} namespace - The namespace of the foreign relation
     * @param {string} relation - The relation name within the namespace
     * @param {Array<string>} properties - Properties to lazily expand from the referenced relation
     * @param {Object} [specs] - Additional specs for the foreign key
     * @param {string} [specs.id] - Unique identifier for this foreign key reference
     * @param {string} [specs.title] - Title for the foreign key reference
     * @param {boolean} [specs.required] - Whether the foreign key is required
     * @param {boolean} [specs.optional] - Whether the foreign key is optional
     * @param {boolean} [specs.editable] - Whether the foreign key can be changed by the user
     * @returns {Object} A partial type definition with the foreign key schema
     */
    registerForeignKey = (namespace, relation, properties = [], specs = {}) => {
        const id = specs.id || ObjectId().toString();
        const title = specs.title || `${namespace}:${relation}`;
        const optional = specs.optional ?? !specs.required;
        const editable = specs.editable ?? true;

        // Create the _id property schema
        const idPropertySchema = Type.String({
            title: '_id',
            'x-dataType': 'string',
            'x-propertyName': '_id',
            'x-prettyName': 'Unique ID',
            'x-editable': false
        });

        // Create the base foreign key object schema. Optional fields must be wrapped with Type.Optional
        // so the parent Type.Object does not mark them as required.
        const foreignKeySchema = Type.Object(
            {
                _id: idPropertySchema
            },
            {
                title: title,
                'x-id': id,
                'x-dataType': 'object',
                'x-propertyName': `${namespace}:${relation}`,
                ...(typeof specs.optional === 'boolean' || typeof specs.required === 'boolean'
                    ? { 'x-optional': !!optional }
                    : {}),
                ...(typeof specs.required === 'boolean' ? { 'x-required': specs.required } : {}),
                'x-editable': !!editable,
                'x-namespace': namespace,
                'x-relation': relation,
                additionalProperties: true
            }
        );

        const propertySchema = optional ? Type.Optional(foreignKeySchema) : foreignKeySchema;
        const result = { [`${namespace}:${relation}`]: propertySchema };

        // Register a deferred expansion step.
        // At this point we only know the FK shape (_id + requested lazy props), while the
        // complete relation schema may be discovered later by other blocks in the tree.
        this.callbacks.push(dataDictionary => {
            const origDefinition = findElement(id, dataDictionary);
            if (!origDefinition) return;
            if (!origDefinition.properties) return;

            const sourceRelation = dataDictionary[namespace]?.[relation];
            const fallbackRequiredProperties = getFallbackRequiredProperties(origDefinition);
            const requiredToAdd = new Set();

            properties.forEach(prop => {
                const sourceProperty = getSourceProperty(sourceRelation, prop);
                if (sourceProperty) {
                    ensureProperty(origDefinition, prop, sourceProperty);
                } else {
                    // A metadata page can reference a relation that is not otherwise modeled in
                    // this use case. Keep a conservative placeholder so OpenAPI still documents
                    // the display contract instead of silently dropping the selected fields.
                    ensureFallbackStringProperty(origDefinition, prop);
                }

                // Preferred path: mirror required[] from the source relation.
                // Fallback path: if the source relation is missing, require only input-display props
                // (or dropdown-display props when no input-display list exists).
                if (
                    isRequiredProperty(sourceRelation, prop) ||
                    (!sourceProperty && fallbackRequiredProperties.has(prop))
                ) {
                    requiredToAdd.add(prop);
                }
            });

            mergeRequiredProperties(origDefinition, requiredToAdd);
        });

        return result;
    };
};

/**
 * Recursively searches for an element in the data dictionary by its x-id.
 * @param {string} id - The x-id to search for
 * @param {Object} dataDictionary - The data dictionary to search within
 * @returns {Object|undefined} The found element or undefined
 */
function findElement(id, dataDictionary) {
    if (!dataDictionary) return;
    if (dataDictionary['x-id'] === id) {
        return dataDictionary;
    }
    // Search in properties of TypeBox Object schemas
    if (dataDictionary.properties) {
        for (const prop in dataDictionary.properties) {
            const result = findElement(id, dataDictionary.properties[prop]);
            if (result) return result;
        }
    }
    // Also search in top-level properties (for namespace/relation structure)
    for (const prop in dataDictionary) {
        if (prop !== 'properties' && typeof dataDictionary[prop] === 'object') {
            const result = findElement(id, dataDictionary[prop]);
            if (result) return result;
        }
    }
}

function addRequiredProperty(schema, propertyName) {
    mergeRequiredProperties(schema, new Set([propertyName]));
}

function mergeRequiredProperties(schema, requiredToAdd) {
    if (!requiredToAdd || requiredToAdd.size === 0) return;

    const existingRequired = new Set(Array.isArray(schema.required) ? schema.required : []);
    requiredToAdd.forEach(propertyName => {
        const nestedPath = parseNestedProperty(propertyName);
        if (!nestedPath) {
            existingRequired.add(propertyName);
            return;
        }

        // JSON Schema requires each object level to declare its own required fields.
        // Requiring only `metaui:useCase.title` at the parent level would validate a
        // flat key that real Backbone payloads do not send.
        existingRequired.add(nestedPath.objectName);
        const nestedSchema = ensureNestedObjectProperty(schema, nestedPath.objectName);
        nestedSchema.required = Array.from(new Set([...(nestedSchema.required || []), nestedPath.propertyName])).sort();
    });
    schema.required = [...existingRequired].sort();
}

function getFallbackRequiredProperties(schema) {
    const inputDisplayProperties = schema['x-inputDisplayProperties'] || [];
    if (inputDisplayProperties.length) {
        return new Set(inputDisplayProperties);
    }
    return new Set(schema['x-dropdownDisplayProperties'] || []);
}

function getSourceProperty(sourceRelation, prop) {
    const directProperty = sourceRelation?.properties?.[prop] ?? sourceRelation?.[prop];
    if (directProperty) return directProperty;

    // Prefer the source relation's nested schema when it exists so generated foreign-key
    // references inherit real constraints such as minLength/maxLength instead of using
    // the fallback string placeholder.
    const nestedPath = parseNestedProperty(prop);
    if (!nestedPath) return;

    const objectProperty =
        sourceRelation?.properties?.[nestedPath.objectName] ?? sourceRelation?.[nestedPath.objectName];
    return objectProperty?.properties?.[nestedPath.propertyName] ?? objectProperty?.[nestedPath.propertyName];
}

function isRequiredProperty(sourceRelation, prop) {
    const sourceRequired = new Set(Array.isArray(sourceRelation?.required) ? sourceRelation.required : []);
    if (sourceRequired.has(prop)) return true;

    // Required status also has to follow the nested schema shape. A required child field
    // only matters when the containing object is required by the referenced relation.
    const nestedPath = parseNestedProperty(prop);
    if (!nestedPath || !sourceRequired.has(nestedPath.objectName)) return false;

    const objectProperty =
        sourceRelation?.properties?.[nestedPath.objectName] ?? sourceRelation?.[nestedPath.objectName];
    return Array.isArray(objectProperty?.required) && objectProperty.required.includes(nestedPath.propertyName);
}

function ensureProperty(schema, prop, propertySchema) {
    const nestedPath = parseNestedProperty(prop);
    if (!nestedPath) {
        schema.properties[prop] = propertySchema;
        return;
    }

    const nestedSchema = ensureNestedObjectProperty(schema, nestedPath.objectName);
    nestedSchema.properties[nestedPath.propertyName] = propertySchema;
}

function ensureFallbackStringProperty(schema, prop) {
    const nestedPath = parseNestedProperty(prop);
    if (nestedPath) {
        const nestedSchema = ensureNestedObjectProperty(schema, nestedPath.objectName);
        if (nestedSchema.properties[nestedPath.propertyName]) return;

        nestedSchema.properties[nestedPath.propertyName] = Type.String({
            title: nestedPath.propertyName,
            'x-dataType': 'string',
            'x-propertyName': nestedPath.propertyName
        });
        return;
    }

    if (schema.properties[prop]) return;
    // If the source relation is unavailable, keep a minimal placeholder for lazy display props
    // so generated schemas still expose the metadata-selected display keys.
    schema.properties[prop] = Type.String({
        title: prop,
        'x-dataType': 'string',
        'x-propertyName': prop
    });
}

function ensureNestedObjectProperty(schema, objectName) {
    if (!schema.properties[objectName]) {
        // Foreign-key summaries intentionally allow extra fields. Existing payloads often
        // carry more than the display fields chosen in metadata, and schema generation
        // should not reject those older or richer records.
        schema.properties[objectName] = Type.Object(
            {},
            {
                title: objectName,
                'x-dataType': 'object',
                'x-propertyName': objectName,
                additionalProperties: true
            }
        );
    }

    if (!schema.properties[objectName].properties) {
        schema.properties[objectName].properties = {};
    }

    return schema.properties[objectName];
}

function parseNestedProperty(prop) {
    // Metadata uses `namespace:relation.property` as a display selector. Treat only that
    // one-level form as a nested JSON path; deeper paths have no established payload
    // contract here and should remain unchanged until the data model defines one.
    if (typeof prop !== 'string' || !prop.includes(':') || !prop.includes('.')) return;

    const [objectName, ...propertyParts] = prop.split('.');
    if (propertyParts.length !== 1 || !propertyParts[0]) return;

    return {
        objectName,
        propertyName: propertyParts[0]
    };
}

// Exposed for targeted unit testing of helper behavior.
module.exports._private = {
    addRequiredProperty,
    mergeRequiredProperties,
    getFallbackRequiredProperties,
    getSourceProperty,
    isRequiredProperty,
    ensureProperty,
    ensureFallbackStringProperty,
    ensureNestedObjectProperty,
    parseNestedProperty
};
